// DropdownSelect.tsx
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useDetectOutsideClick } from '../../../../../generic/hooks/useDetectOutsideClick';

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 260px;
  font-size: 14px;

`;

const Trigger = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ $isOpen }) => ($isOpen ? '#1d4ed8' : '#d1d5db')};
  background: #fff;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #4b5563;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.7);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Placeholder = styled.span`
  color: #6b7280;
`;

const Value = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled.span<{ $isOpen: boolean }>`
  margin-left: 8px;
  font-size: 10px;
  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
  transition: transform 0.15s ease;
`;


const List = styled.ul<{ $isOpen: boolean, $isTop?: boolean, $top?: number, $left?: number, $width?: number }>`
  position: fixed;
  z-index: 9999;
  top: ${({ $top, $isTop }) => $isTop ? 'auto' : `${$top}px`};
  bottom: ${({ $top, $isTop }) => $isTop ? `calc(100vh - ${$top}px)` : 'auto'};
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  max-height: 220px;
  margin: 0;
  /* padding: 4px 0; */
  list-style: none;
  border-radius: 8px;
  border: 1px solid #374151;
  background: #fff;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
  overflow-y: auto;

  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transform: scaleY(${({ $isOpen }) => ($isOpen ? 1 : 0.95)});
  transform-origin: top;
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 0.12s ease, transform 0.12s ease;
`;

const Item = styled.li<{ $isSelected: boolean }>`
  padding: 0.5em;
  cursor: pointer;
  color: ${({ $isSelected }) => ($isSelected ? '#fff' : '#000')};
  background: ${({ $isSelected }) => ($isSelected ? '#80E081' : 'transparent')};
  font-weight: ${({ $isSelected }) => ($isSelected ? 500 : 400)};
  display: flex;
  justify-content: flex-end;
  &:hover {
    color: ${({ $isSelected }) => ($isSelected ? '#fff' : '#000')};
    background: ${({ $isSelected }) => ($isSelected ? '#80E081' : '#7AB0AB')};
  }
`;

export type DropdownSelectProps<
  T extends object,
  V extends keyof T,
  L extends keyof T
> = {
  /** Список объектов */
  items: T[];
  /** Ключ, по которому храним/ищем значение (id, code и т.п.) */
  valueKey: V;
  /** Ключ, который отображаем как текст */
  labelKey: L;
  /** Дефолтное значение по valueKey (например, id) */
  defaultValue?: T[V];
  /** Колбэк с выбранным объектом */
  onChange: (item: T) => void;
  /** Placeholder, когда ничего не выбрано */
  placeholder?: string;
  /** Заблокировать селект */
  disabled?: boolean;
  /** Кастомный ключ для React (если надо) */
  getKey?: (item: T) => React.Key;
  /** Показать список сверху */
  isTop?: boolean;
};



// ... остальные компоненты ...

export function DropdownSelect<
  T extends object,
  V extends keyof T,
  L extends keyof T
>({
  items,
  valueKey,
  labelKey,
  defaultValue,
  onChange,
  placeholder = 'Select…',
  disabled,
  getKey,
  isTop,
}: DropdownSelectProps<T, V, L>) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<T | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Установка дефолтного значения
  useEffect(() => {
    if (defaultValue === undefined) return;
    const found = items.find((item) => item[valueKey] === defaultValue) ?? null;
    setSelected(found);
  }, [defaultValue, items, valueKey]);

  // Обновление позиции при открытии
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: isTop ? rect.top : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    // update position when window is resized or scrolled
    
  }, [isOpen, isTop]);

  // Закрытие по клику вне
 

  const toggleOpen = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (item: T) => {
    setSelected(item);
    setIsOpen(false);
    onChange(item);
  };

  const selectedLabel =
    selected != null ? (selected[labelKey] as unknown as React.ReactNode) : null;

  const listContent = (
    <List 
      role="listbox" 
      $isOpen={isOpen} 
      $isTop={isTop}
      $top={position.top}
      $left={position.left}
      $width={position.width}
    >
      {items.map((item) => {
        const value = item[valueKey];
        const label = item[labelKey] as unknown as React.ReactNode;
        const isSelected =
          selected != null && selected[valueKey] === value;

        return (
          <Item
            key={getKey ? getKey(item) : String(value)}
            $isSelected={isSelected}
            onMouseDown={(e) => {
              e.stopPropagation()
              handleSelect(item)
            }}
            role="option"
            aria-selected={isSelected}
          >
            {label}
          </Item>
        );
      })}
    </List>
  );
  useDetectOutsideClick(wrapperRef, () => setIsOpen(false));
  return (
    <Wrapper onClick={(e) => e.stopPropagation()} ref={wrapperRef}>
      <Trigger
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        disabled={disabled}
        $isOpen={isOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedLabel ? (
          <Value>{selectedLabel}</Value>
        ) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
        <Chevron $isOpen={isOpen}>▾</Chevron>
      </Trigger>

      {isOpen && createPortal(listContent, document.body)}
    </Wrapper>
  );
}